import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LazyLoadingFallback from './components/common/LazyLoadingFallback';
import { useAuth } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import { useTheme } from './contexts/ThemeContext';
import { getTheme } from './theme';
import { getDefaultPathForRole, isAdminRole, isDesignerOrAdminRole, PERMISSIONS, hasPermission } from './utils/roleConfig';
import { preloadPagesByRole, preloadCommonPages } from './utils/preloader';
import { logNetworkInfo } from './utils/networkDetection';

// Lazy load all page components
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const ClientsPage = React.lazy(() => import('./pages/ClientsPage'));
const MeasurementsPage = React.lazy(() => import('./pages/MeasurementsPage'));
const MeasurementDetailsPage = React.lazy(() => import('./pages/MeasurementDetailsPage'));
const PublicMeasurementDetailsPage = React.lazy(() => import('./pages/PublicMeasurementDetailsPage'));
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailsPage = React.lazy(() => import('./pages/ProjectDetailsPage'));
const DressDetailsPage = React.lazy(() => import('./pages/DressDetailsPage'));
const EmployeesPage = React.lazy(() => import('./pages/EmployeesPage'));
const EmployeeDetailsPage = React.lazy(() => import('./pages/EmployeeDetailsPage'));
const StagesPage = React.lazy(() => import('./pages/StagesPage'));
const VirtualFittingPage = React.lazy(() => import('./pages/VirtualFittingPage'));
const EmployeeDashboardPage = React.lazy(() => import('./pages/EmployeeDashboardPage'));
const AdminAssignmentsPage = React.lazy(() => import('./pages/AdminAssignmentsPage'));
const EmployeeAssignmentsOverviewPage = React.lazy(() => import('./pages/EmployeeAssignmentsOverviewPage'));
const EmployeeCompetitiveDashboardPage = React.lazy(() => import('./pages/EmployeeCompetitiveDashboardPage'));
const TimeReportsPage = React.lazy(() => import('./pages/TimeReportsPage'));
const AttendanceDashboardPage = React.lazy(() => import('./pages/AttendanceDashboardPage'));
const AttendanceDayDetailPage = React.lazy(() => import('./pages/AttendanceDayDetailPage'));
const InvoicesPage = React.lazy(() => import('./pages/InvoicesPage'));
const GSTConfigurationPage = React.lazy(() => import('./pages/GSTConfigurationPage'));
const BrandSettingsPage = React.lazy(() => import('./pages/BrandSettingsPage'));
const BrandManagementPage = React.lazy(() => import('./pages/BrandManagementPage'));
const HSNMasterPage = React.lazy(() => import('./pages/HSNMasterPage'));
const PaymentsPage = React.lazy(() => import('./pages/PaymentsPage'));
const CreditNotesPage = React.lazy(() => import('./pages/CreditNotesPage'));
const GSTReportsPage = React.lazy(() => import('./pages/GSTReportsPage'));

// Component to handle default route based on user role
const DefaultRoute: React.FC = () => {
    const { user, isLoading } = useAuth();
    console.log('=== DEFAULT ROUTE DEBUG ===');
    console.log('DefaultRoute called with user:', user);
    console.log('User role:', user?.role);
    console.log('Role name:', user?.role?.name);
    console.log('Is loading:', isLoading);
    
    // Wait for user data to be fully loaded before redirecting
    if (isLoading || !user || !user.role || !user.role.name) {
        console.log('DefaultRoute waiting for complete user data...');
        return <LazyLoadingFallback message="Loading user data..." />;
    }
    
    const redirectPath = getDefaultPathForRole(user.role.name);
    console.log('Redirecting to:', redirectPath);
    
    return <Navigate to={redirectPath} replace />;
};

// Component to protect admin-only routes
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isLoading } = useAuth();
    
    // Wait for user data to be fully loaded before checking roles
    if (isLoading || !user || !user.role || !user.role.name) {
        return <LazyLoadingFallback message="Verifying permissions..." />;
    }
    
    // Redirect non-admin users to assignments page
    if (!isAdminRole(user.role.name)) {
        return <Navigate to="/assignments" replace />;
    }
    
    return <>{children}</>;
};

// Component to protect routes that require Designer or Admin access
const DesignerOrAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isLoading } = useAuth();
    
    // Wait for user data to be fully loaded before checking roles
    if (isLoading || !user || !user.role || !user.role.name) {
        return <LazyLoadingFallback message="Verifying permissions..." />;
    }
    
    // Redirect users who are neither admin nor designer to assignments page
    if (!isDesignerOrAdminRole(user.role.name)) {
        return <Navigate to="/assignments" replace />;
    }
    
    return <>{children}</>;
};

// Generic permission-based route protection
const PermissionRoute: React.FC<{ children: React.ReactNode; permission: string }> = ({ children, permission }) => {
    const { user, isLoading } = useAuth();
    
    console.log('PermissionRoute check:', { permission, user: user?.role?.name, isLoading });
    
    // Wait for user data to be fully loaded before checking permissions
    if (isLoading || !user || !user.role || !user.role.name) {
        console.log('PermissionRoute waiting for complete user data...');
        return <LazyLoadingFallback message="Verifying permissions..." />;
    }
    
    // Redirect users who don't have the required permission to assignments page
    if (!hasPermission(user.role.name, permission)) {
        console.log('Permission denied, redirecting to assignments');
        return <Navigate to="/assignments" replace />;
    }
    
    console.log('Permission granted, rendering children');
    return <>{children}</>;
};

const AppWithTheme: React.FC = () => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const { mode } = useTheme();
    const theme = getTheme(mode);

    // Preload pages based on user role after authentication
    React.useEffect(() => {
        // Log network conditions for debugging
        logNetworkInfo();
        
        if (isAuthenticated && user?.role?.name) {
            preloadPagesByRole(user.role.name);
        }
        
        // Always preload common pages after initial load
        preloadCommonPages();
    }, [isAuthenticated, user?.role?.name]);

    if (isLoading) {
        return <LazyLoadingFallback message="Initializing application..." />;
    }

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <Suspense fallback={<LazyLoadingFallback />}>
                <Routes>
                    <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <DefaultRoute />} />
                    <Route path="/public/measurements/:id" element={<PublicMeasurementDetailsPage />} />
                    
                    {/* Public leaderboard - accessible to everyone */}
                    <Route path="/leaderboard" element={<EmployeeCompetitiveDashboardPage />} />
                    
                    <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route index element={<PermissionRoute permission={PERMISSIONS.DASHBOARD}><DashboardPage /></PermissionRoute>} />
                        <Route path="clients" element={<PermissionRoute permission={PERMISSIONS.CLIENTS}><ClientsPage /></PermissionRoute>} />
                        <Route path="measurements" element={<PermissionRoute permission={PERMISSIONS.MEASUREMENTS}><MeasurementsPage /></PermissionRoute>} />
                        <Route path="measurements/:id" element={<PermissionRoute permission={PERMISSIONS.MEASUREMENTS}><MeasurementDetailsPage /></PermissionRoute>} />
                        <Route path="projects" element={<PermissionRoute permission={PERMISSIONS.PROJECTS}><ProjectsPage /></PermissionRoute>} />
                        <Route path="projects/:projectId" element={<PermissionRoute permission={PERMISSIONS.PROJECTS}><ProjectDetailsPage /></PermissionRoute>} />
                        <Route path="projects/:projectId/dresses/:dressId" element={<DressDetailsPage />} />
                        <Route path="employees" element={<PermissionRoute permission={PERMISSIONS.EMPLOYEES}><EmployeesPage /></PermissionRoute>} />
                        <Route path="employees/:employeeId" element={<PermissionRoute permission={PERMISSIONS.EMPLOYEES}><EmployeeDetailsPage /></PermissionRoute>} />
                        <Route path="stages" element={<PermissionRoute permission={PERMISSIONS.STAGES}><StagesPage /></PermissionRoute>} />
                        <Route path="assignments" element={<EmployeeDashboardPage />} />
                        <Route path="admin/assignments" element={<PermissionRoute permission={PERMISSIONS.ADMIN_ASSIGNMENTS}><AdminAssignmentsPage /></PermissionRoute>} />
                        <Route path="admin/employee-assignments" element={<PermissionRoute permission={PERMISSIONS.EMPLOYEE_OVERVIEW}><EmployeeAssignmentsOverviewPage /></PermissionRoute>} />
                        <Route path="admin/employee-assignments/:employeeId" element={<PermissionRoute permission={PERMISSIONS.EMPLOYEE_OVERVIEW}><EmployeeDashboardPage /></PermissionRoute>} />
                        <Route path="admin/time-reports" element={<PermissionRoute permission={PERMISSIONS.TIME_REPORTS}><TimeReportsPage /></PermissionRoute>} />
                        <Route path="admin/attendance" element={<PermissionRoute permission={PERMISSIONS.ATTENDANCE}><AttendanceDashboardPage /></PermissionRoute>} />
                        <Route path="admin/attendance/:date" element={<PermissionRoute permission={PERMISSIONS.ATTENDANCE}><AttendanceDayDetailPage /></PermissionRoute>} />
                        <Route path="virtual-fitting" element={<PermissionRoute permission={PERMISSIONS.VIRTUAL_FITTING}><VirtualFittingPage /></PermissionRoute>} />
                        <Route path="virtual-fitting/:clientId" element={<PermissionRoute permission={PERMISSIONS.VIRTUAL_FITTING}><VirtualFittingPage /></PermissionRoute>} />
                        <Route path="virtual-fitting/:clientId/:projectId" element={<PermissionRoute permission={PERMISSIONS.VIRTUAL_FITTING}><VirtualFittingPage /></PermissionRoute>} />
                        
                        {/* GST Billing Routes */}
                        <Route path="invoices" element={<PermissionRoute permission={PERMISSIONS.INVOICES}><InvoicesPage /></PermissionRoute>} />
                        <Route path="payments" element={<PermissionRoute permission={PERMISSIONS.PAYMENTS}><PaymentsPage /></PermissionRoute>} />
                        <Route path="credit-notes" element={<PermissionRoute permission={PERMISSIONS.CREDIT_NOTES}><CreditNotesPage /></PermissionRoute>} />
                        <Route path="gst/hsn-master" element={<PermissionRoute permission={PERMISSIONS.HSN_MASTER}><HSNMasterPage /></PermissionRoute>} />
                        <Route path="gst/configuration" element={<PermissionRoute permission={PERMISSIONS.GST_CONFIGURATION}><GSTConfigurationPage /></PermissionRoute>} />
                        <Route path="gst/reports" element={<PermissionRoute permission={PERMISSIONS.GST_REPORTS}><GSTReportsPage /></PermissionRoute>} />
                        
                        {/* Brand Management Routes */}
                        <Route path="brand/settings" element={<PermissionRoute permission={PERMISSIONS.BRAND_SETTINGS}><BrandSettingsPage /></PermissionRoute>} />
                        <Route path="admin/brands" element={<PermissionRoute permission={PERMISSIONS.BRAND_MANAGEMENT}><BrandManagementPage /></PermissionRoute>} />
                    </Route>
                    <Route path="*" element={<DefaultRoute />} />
                </Routes>
            </Suspense>
        </MuiThemeProvider>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <AppWithTheme />
        </ThemeProvider>
    );
};

export default App; 